import React, { useMemo, useState } from 'react';
import './PortfolioReport.css';
import {
  Property, LeaseInfo, buildRows, useLeases,
  compactAED, compactEUR, eur, fmtEUR, fmtAEDFull, pct, NET_RENT_RATIO
} from './portfolioMetrics';
import { useLanguage } from './LanguageContext';

/* ================================================================
   REVENUE & RETURN — per-property yield, gross or net.
   Lives in the Portfolio ▸ Performance tab (the report covers the
   portfolio story; this is the return detail).
   ================================================================ */

interface Props {
  properties: Property[];
  investorId?: string;
  dldData?: any;
  /** Sample leases for the showcase — see PortfolioReport.leasesOverride. */
  leasesOverride?: Record<string, LeaseInfo>;
}

const PortfolioRevenue: React.FC<Props> = ({ properties, investorId, dldData, leasesOverride }) => {
  const { language } = useLanguage();
  const de = language === 'de';
  const [mode, setMode] = useState<'gross' | 'net'>('gross');
  const [currency, setCurrency] = useState<'EUR' | 'AED'>('EUR');
  const leases = useLeases(investorId, leasesOverride);
  const units = useMemo(() => properties || [], [properties]);
  const rows = useMemo(() => buildRows(units, dldData, leases), [units, dldData, leases]);

  const earning = rows.filter(r => r.annualRent > 0 || r.estMonthlyRent > 0);
  const leaseRows = rows.filter(r => (r.lease?.annualRent || 0) > 0);
  const leaseCollected = leaseRows.reduce((s, r) => s + (r.lease?.collected || 0), 0);
  const leaseExpenses = leaseRows.reduce((s, r) => s + (r.lease?.expenses || 0), 0);
  const leaseRent = leaseRows.reduce((s, r) => s + r.annualRent, 0);

  const money = (aed: number) => (currency === 'EUR' ? compactEUR(eur(aed)) : compactAED(aed));
  const moneyFull = (aed: number) =>
    currency === 'EUR' ? fmtEUR(Math.round(eur(aed))) : `AED ${fmtAEDFull(aed)}`;

  // Portfolio-level yield on what is actually earning
  const totalRent = earning.reduce((s, r) => s + (r.annualRent > 0 ? r.annualRent : r.estMonthlyRent * 12), 0);
  const totalBasis = earning.reduce((s, r) => s + r.purchase, 0);
  const portfolioYield = totalBasis > 0 ? (totalRent / totalBasis) * 100 : 0;

  return (
    <div className="pr-root pr-embedded">
      <div className="pr-sec-head">
        <h2 className="pr-sec-title pr-serif">{de ? 'Ertrag & Rendite' : 'Revenue & return'}</h2>
        <span className="pr-sec-line" />
      </div>

      <div className="pr-table">
        <div className="pr-toolbar">
          <span className="pr-toggle">
            {(['gross', 'net'] as const).map(m => (
              <button key={m} data-on={mode === m} onClick={() => setMode(m)}>{de ? (m === 'gross' ? 'brutto' : 'netto') : m}</button>
            ))}
          </span>
          <span className="pr-toggle">
            {(['EUR', 'AED'] as const).map(c => (
              <button key={c} data-on={currency === c} onClick={() => setCurrency(c)}>{c}</button>
            ))}
          </span>
        </div>

        {earning.length === 0 ? (
          <p className="pr-empty">
            {de
              ? 'Mietrenditen erscheinen, sobald die erste Einheit vermietet ist oder DLD-Mietvergleiche vorliegen.'
              : 'Rental returns appear as soon as the first unit is leased or DLD rental comparables are synced.'}
          </p>
        ) : (
          <>
            <div className="pr-yield" style={{ background: '#fbfaf7' }}>
              <div className="pr-yield-head">
                <div>
                  <p className="pr-label">
                    {de
                      ? `Portfolio · ${mode === 'gross' ? 'Brutto' : 'Netto'}rendite der Einheiten mit Miete oder DLD-Schätzung`
                      : `Portfolio · ${mode} yield on units with rent or a DLD estimate`}
                  </p>
                  <div className="pr-tr-sub" style={{ marginTop: 4 }}>
                    {de
                      ? `${earning.length} von ${rows.length} Einheiten · ${money(totalRent)} Miete p.a.`
                      : `${earning.length} of ${rows.length} units · ${money(totalRent)} rent pa`}
                  </div>
                </div>
                <span className="pr-yield-pct pr-serif pr-gold-t">
                  {pct(mode === 'net' ? portfolioYield * NET_RENT_RATIO : portfolioYield)}
                </span>
              </div>
            </div>

            {earning.map(r => {
              const annual = r.annualRent > 0 ? r.annualRent : r.estMonthlyRent * 12;
              // real expenses from Property Management when we have them,
              // otherwise the flat service-charge assumption
              const net = r.lease && r.lease.expenses > 0
                ? Math.max(0, annual - r.lease.expenses)
                : annual * NET_RENT_RATIO;
              const shown = mode === 'net' ? net : annual;
              const y = r.purchase > 0 ? (shown / r.purchase) * 100 : 0;
              const source = r.lease
                ? `${de ? 'Vermietet (Vertrag)' : 'Leased'}${r.lease.tenant ? ` · ${r.lease.tenant}` : ''}`
                : r.rented ? (de ? 'Vermietet' : 'Rented') : (de ? 'Geschätzt (DLD)' : 'Estimated');
              const typeLabel = r.unit.type
                ? (de ? String(r.unit.type).replace(/^Apartment$/i, 'Wohnung').replace(/^Villa$/i, 'Villa').replace(/^Townhouse$/i, 'Reihenhaus') : r.unit.type)
                : (de ? 'Einheit' : 'Unit');
              return (
                <div key={r.unit.id} className="pr-yield">
                  <div className="pr-yield-head">
                    <div style={{ minWidth: 0 }}>
                      <div className="pr-tr-name">{r.unit.name || r.unit.project}</div>
                      <div className="pr-tr-sub">
                        {typeLabel} · {source} ·{' '}
                        {compactEUR(eur(shown))} / {compactAED(shown)} {de ? 'p.a.' : 'pa'}
                      </div>
                    </div>
                    <span className="pr-yield-pct pr-serif pr-gold-t">{pct(y)}</span>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div className="pr-bar">
                      <span className="pr-bar-fill" style={{ width: `${Math.max(0, Math.min(100, y / 0.2))}%` }} />
                    </div>
                    <div className="pr-scale">
                      <span>0%</span><span>10%</span><span>20%</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {leaseRows.length > 0 && (
              <div className="pr-yield" style={{ background: '#fdfcf9' }}>
                <div className="pr-yield-head">
                  <div>
                    <p className="pr-label">{de ? 'Hausverwaltung · unterzeichnete Mietverträge' : 'Property management · signed leases'}</p>
                    <div className="pr-tr-sub" style={{ marginTop: 4 }}>
                      {de
                        ? `${leaseRows.length} ${leaseRows.length === 1 ? 'Vertrag' : 'Verträge'} · ${moneyFull(leaseCollected)} eingegangen · ${leaseExpenses > 0 ? `${moneyFull(leaseExpenses)} Ausgaben` : 'keine Ausgaben erfasst'}`
                        : `${leaseRows.length} lease${leaseRows.length === 1 ? '' : 's'} · ${moneyFull(leaseCollected)} collected · ${leaseExpenses > 0 ? `${moneyFull(leaseExpenses)} expenses` : 'no expenses booked'}`}
                    </div>
                  </div>
                  <span className="pr-serif pr-green" style={{ fontSize: '1.15rem', whiteSpace: 'nowrap' }}>
                    {money(leaseRent)} {de ? 'p.a.' : 'pa'}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <p className="pr-foot">
          {de
            ? `Brutto = Jahresmiete ÷ Kaufpreis. Netto nutzt die in der Hausverwaltung erfassten Ausgaben, wo ein Mietvertrag besteht; sonst werden ${Math.round((1 - NET_RENT_RATIO) * 100)} % für Servicegebühren, Verwaltung und Instandhaltung angenommen. „Vermietet“ stammt aus unterzeichneten Mietverträgen; „Geschätzt“ aus DLD-Ejari-Vergleichsmieten — keine tatsächliche Einnahme.`
            : `Gross = annual rent ÷ purchase price. Net uses actual expenses booked in Property Management where a lease exists, otherwise it assumes ${Math.round((1 - NET_RENT_RATIO) * 100)}% for service charges, management and maintenance. “Leased” figures come from signed tenancy contracts; “Estimated” uses DLD Ejari comparables — not actual income.`}
        </p>
      </div>
    </div>
  );
};

export default PortfolioRevenue;
